import { useMemo } from 'react';

import { ColumnDef, Icon, Link, RyeconLineage } from '@dbt-labs/sourdough';

import { CustomCell } from '@/components/Table/CustomCell';
import { OwnerHeader } from '@/components/Table/OwnerHeader';
import { TwoLineHeader } from '@/components/Table/TableHeader';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { ModelAppliedListNode } from '@/hooks/discovery/appliedModels';
import { PublicModelResult } from '@/hooks/discovery/publicModels';
import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { targetResourceSelector } from '@/util/selectorGenerators';

type ProducedColumnsProps = {
  disableGroupsColumn: boolean;
};

export const useProducedPublicModelsColumns = ({
  disableGroupsColumn,
}: ProducedColumnsProps) => {
  const getLineageLink = useLinkToExplorer('projectLineage');
  const getProjectLink = useLinkToExplorer('project');
  const { accountId, projectId, environmentType } = useUrlProjectParams();
  const getDetailsPageLink = useLinkToExplorer('resourceDetails');
  const { getProjectName } = useDbtCloudAccount();

  const columns = useMemo(() => {
    return [
      {
        header: () => <TwoLineHeader title="Name" />,
        accessorKey: 'name',
        accessorFn: (data) => ({
          uniqueId: data.uniqueId,
          name: data.name,
        }),
        cell: (cellContext) => {
          const value = cellContext.getValue() as {
            uniqueId: string | null;
            name: string | null;
          } | null;
          return (
            <CustomCell tooltip={value?.name || ''}>
              <Link
                isInternal
                to={getDetailsPageLink({
                  accountId,
                  environmentType,
                  projectId,
                  dbtUniqueId: value?.uniqueId || '',
                })}
                className="truncate"
              >
                {value?.name}
              </Link>
            </CustomCell>
          );
        },
      },
      {
        accessorKey: 'dependentProjects',
        header: () => <TwoLineHeader title="Used in projects" />,
        accessorFn: (row) => {
          if (!row.dependentProjects?.length) return null;
          const [first, ...remaining] = row.dependentProjects;
          const remainingSuffix = remaining.length ? `, +${remaining.length}` : '';
          return {
            label: `${getProjectName(first?.projectId) || first?.dbtCoreProject}`,
            suffix: remainingSuffix,
            projectId: first?.projectId,
          };
        },
        cell: (cellContext) => {
          const value = cellContext.getValue() as {
            label: string | null;
            suffix: string | null;
            projectId: number | null;
          } | null;
          const label: string | null = value?.label ?? null;
          const projectId: number | null = value?.projectId ?? null;
          if (!label || !projectId) return null;
          return (
            <>
              <Link
                isInternal
                to={getProjectLink({
                  accountId,
                  projectId: projectId,
                  // Account level projects list only links to production, this is not entirely correct
                  // correct logic would be to get the environment id from the dependency and get the correct environment
                  environmentType: 'production',
                })}
              >
                {label}
              </Link>
              {value?.suffix}
            </>
          );
        },
        size: 100,
      },
      {
        accessorKey: 'tags',
        accessorFn: (data) => data.tags?.join(', '),
        header: () => <TwoLineHeader title="Tags" />,
        size: 100,
      },
      {
        accessorKey: 'group',
        accessorFn: (data) => ({
          packageName: data.packageName,
          group: data.group,
        }),
        header: OwnerHeader,
        cell: (cellContext) => {
          const value = cellContext.getValue() as {
            packageName: string | null;
            group: string | null;
          } | null;
          const group: string | null = value?.group ?? null;
          const packageName: string | null = value?.packageName ?? null;
          const children =
            !group || !packageName ? null : (
              <Link
                isInternal
                to={getDetailsPageLink({
                  accountId,
                  environmentType,
                  projectId,
                  dbtUniqueId: `group.${packageName}.${group}`,
                })}
              >
                {group}
              </Link>
            );
          return (
            <CustomCell disable={disableGroupsColumn} tooltip={group ?? ''}>
              {children}
            </CustomCell>
          );
        },
        size: 100,
      },
      {
        accessorKey: 'uniqueId',
        accessorFn: (data) => ({
          uniqueId: data.uniqueId,
          fqn: data.fqn,
        }),
        header: () => <TwoLineHeader title="Lineage" />,
        maxSize: 70,
        minSize: 70,
        size: 46,
        cell: (cellContext) => {
          const value = cellContext.getValue() as {
            uniqueId: string | null;
            fqn: string[] | null;
          } | null;
          const fqn: string[] | null = value?.fqn ?? null;
          const uniqueId: string = value?.uniqueId ?? '';

          if (!fqn) return null;
          return (
            <Link
              className="mx-auto"
              isInternal
              to={getLineageLink({
                dbtUniqueId: uniqueId,
                accountId,
                environmentType,
                projectId,
                selector: targetResourceSelector({
                  fqn,
                  depth: 2,
                }),
              })}
            >
              <Icon ryecon={RyeconLineage} />
            </Link>
          );
        },
      },
    ] satisfies ColumnDef<
      ModelAppliedListNode & Pick<PublicModelResult, 'dependentProjects'>
    >[];
  }, [
    accountId,
    disableGroupsColumn,
    environmentType,
    getDetailsPageLink,
    getLineageLink,
    getProjectLink,
    getProjectName,
    projectId,
  ]);
  return columns;
};
